import { useLayoutEffect, useState, useRef, useContext } from 'react';
import { Box, Text } from '@rsos/base-components';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import { IconWrapper, ToolTip, TooltipChild } from './ActionBar.styles';

export const DEFAULT_ICON_WIDTH = 17;
const ADDITIONAL_SPACING = 2;

/**
 * Action bar item sub-component.
 * @param {Node} Children - Component to be rendered inside the action bar item.
 * @param {String} iconWidth - Icon width for the action bar item icon.
 * @param {Function} onClick - Callback function for the action bar item click handler.
 * @param {Boolean} shouldRender - Determines if the component should render in the action bar.
 * @param {Node|String} tooltipText - Action bar item tooltip content.
 * @param {String} iconDataName - Data name for the action bar item icon.
 */
export const ActionBarItem = ({
  children = null,
  iconWidth = DEFAULT_ICON_WIDTH,
  onClick = () => {},
  shouldRender = true,
  tooltipText = '',
  isActive = false,
  color = 'secondaryLink',
  iconDataName = 'action-bar-item-icon',
  ...props
}) => {
  const toolTipRef = useRef(null);
  const buttonRef = useRef(null);

  const { layout } = useContext(LayoutContext);

  const [compensation, setCompensation] = useState(0);

  useLayoutEffect(() => {
    if (!layout?.body?.width || !buttonRef?.current || !toolTipRef.current) {
      return;
    }
    const tooltipWidth = toolTipRef.current.offsetWidth;
    const viewportWidth = layout.body.width;
    const buttonRect = buttonRef.current.getBoundingClientRect();

    // Calculate the tooltip's left and right position before the -50% translation gets applied in the styled component
    const tooltipLeft = buttonRect.left + buttonRect.width / 2;
    const tooltipRight = tooltipLeft + tooltipWidth / 2;
    let newCompensation = 0;

    const rightOverlap = tooltipRight - viewportWidth;
    if (rightOverlap > 0) {
      newCompensation = rightOverlap + ADDITIONAL_SPACING;
    }

    setCompensation(newCompensation);
  }, [layout?.body?.width]);

  if (!shouldRender) {
    return null;
  }

  return (
    <Box data-name="action-bar-item" onClick={onClick} pb={3} pl={3} {...props}>
      <ToolTip ref={buttonRef}>
        {tooltipText && (
          <TooltipChild
            data-name="action-bar-item-tooltip"
            ref={toolTipRef}
            overlapCompensation={compensation}
          >
            <Text size="small">{tooltipText}</Text>
          </TooltipChild>
        )}
        <IconWrapper
          isActive={isActive}
          data-name={iconDataName}
          iconWidth={iconWidth}
          color={color}
        >
          {children}
        </IconWrapper>
      </ToolTip>
    </Box>
  );
};
